export const groupAndDeduplicateMessages = (hotelMessage) => {
    const groupedHotelMessage = hotelMessage.reduce((acc, message) => {
        let type = message.RestrictionType || "N/A";

        if (type === 'Unknown') {
            type = 'Other';
        }

        const description = message.Description;

        if (!acc[type]) {
            acc[type] = new Set();
        }

        acc[type].add(description);
        return acc;
    }, {});

    // Convert Sets to Arrays and sort by priority
    const entries = Object.entries(groupedHotelMessage).map(([type, descriptionsSet]) => [
        type,
        Array.from(descriptionsSet)
    ]);

    // Sort: Meals first, Other last, rest alphabetically in between
    entries.sort(([typeA], [typeB]) => {
        if (typeA === 'Meals') return -1;
        if (typeB === 'Meals') return 1;
        if (typeA === 'Other') return 1;
        if (typeB === 'Other') return -1;
        return typeA.localeCompare(typeB);
    });

    // Convert back to object
    return entries.reduce((acc, [type, descriptions]) => {
        acc[type] = descriptions;
        return acc;
    }, {});
};

export const parseStructuredDescription = (description, type) => {
    if (type === 'Meals') {
        const lines = description.split(/\\r\\n|[\r\n]/).map(l => l.trim()).filter(l => l);
        const parsed = { name: '', amount: '', chargeBase: '', ageRange: '' };

        lines.forEach(line => {
            if (line.startsWith('Amount:')) {
                parsed.amount = line.replace('Amount:', '').trim();
            } else if (line.startsWith('Charge Base:')) {
                parsed.chargeBase = line.replace('Charge Base:', '').trim();
            } else if (line.startsWith('Applicable for ages:')) {
                parsed.ageRange = line.replace('Applicable for ages:', '').trim();
            } else if (parsed.name === '') {
                parsed.name = line;
            }
        });
        return parsed;
    }

    // For non-Meals: try to detect complex promotion format
    const lines = description.split(/\\r\\n|[\r\n]/).map(l => l.trim()).filter(l => l.length > 0);

    // Detect if this looks like a structured promotion
    const hasBookingPeriod = lines.some(l => l.startsWith('Booking Period:'));
    const hasTravelPeriod = lines.some(l => l.startsWith('Travel Period:'));
    const hasAmount = lines.some(l => l.startsWith('Amount:'));
    const hasChargeBase = lines.some(l => l.startsWith('Charge Base:'));

    if (hasBookingPeriod || hasTravelPeriod || hasAmount || hasChargeBase) {
        // This is a complex structured promotion
        let mainTextLines = [];
        let bookingPeriod = null;
        let travelPeriod = null;
        let amount = null;
        let chargeBase = null;

        lines.forEach(line => {
            if (line.startsWith('Booking Period:')) {
                bookingPeriod = line.replace('Booking Period:', '').trim();
            } else if (line.startsWith('Travel Period:')) {
                travelPeriod = line.replace('Travel Period:', '').trim();
            } else if (line.startsWith('Amount:')) {
                amount = line.replace('Amount:', '').trim();
            } else if (line.startsWith('Charge Base:')) {
                chargeBase = line.replace('Charge Base:', '').trim();
            } else {
                mainTextLines.push(line.replace(/^[•\-*]\s*/, '').trim());
            }
        });

        return {
            isComplex: true,
            mainText: mainTextLines.join('\n'),
            bookingPeriod,
            travelPeriod,
            amount,
            chargeBase
        };
    }

    // Fallback: simple bullet points
    return lines.map(line => line.replace(/^[•\-*]\s*/, '').trim());
};

// Hotel messages ------------------------------
export const getRoomMessages = (room, hotel) => {
    if (!room.HotelMessage) {
        return [];
    }

    return hotel.HotelMessage.filter(restriction =>
        room.Restrictions.includes(restriction.Id)
    );
};

// Helper function to remove duplicate messages based on ID
export const getUniqueMessages = (messages) => {
    const seen = new Set();
    return messages.filter(message => {
        if (seen.has(message.Id)) {
            return false;
        }
        seen.add(message.Id);
        return true;
    });
};

// Service messages -----------------------------
export const getUnitMessages = (unit, service) => {
    if (!unit.ServiceMessage) {
        return [];
    }

    return service.ServiceMessage.filter(restriction =>
        unit.Restrictions.includes(restriction.Id)
    );
};

export const getUniqueRestrictionsByContent = (restrictions = []) => {
  const map = new Map();

  restrictions.forEach(r => {
    const key = `${r.RestrictionType}|${r.Description}`;
    if (!map.has(key)) {
      map.set(key, r);
    }
  });

  return Array.from(map.values());
};

export const groupRestrictionsByType = (restrictions = []) =>
  restrictions.reduce((grouped, restriction) => {
    const type = restriction.RestrictionType !== 'Unknown' ? restriction.RestrictionType : 'Others';

    if (!grouped[type]) {
      grouped[type] = [];
    }

    grouped[type].push(restriction);
    return grouped;
  }, {});
  